import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * A CURRENCY CODE BOX THAT SUGGESTS — "Short text", with the tenant's own
 * currencies offered underneath it.
 *
 * FIN-09 field 5 and MD-08 both specify this field as *"Short text"*, and both
 * screens honoured that by shipping a bare `<input>` with nothing behind it —
 * one of them seeded with the literal `'USD'`. That is the mirror of the defect
 * on the `<select>` screens: they offered six codes and refused the rest, while
 * these two offered NOTHING and accepted anything, including a typo that no
 * rate table will ever match.
 *
 * WHY THIS IS NOT `<Typeahead>`, which would otherwise be the obvious reuse:
 * that control is id-based and commits only when a row is chosen, so anything
 * typed that matches no option is discarded on blur. For a picker over records
 * that is correct — you cannot invent a customer. For a field the spec calls
 * "Short text" it is a silent data loss, and it would make a legitimate code
 * the tenant has not added yet unenterable. So the VALUE HERE IS THE TEXT: the
 * suggestions are help, not a gate. Which codes are ultimately acceptable is
 * the server's judgement (`currency-guard.ts`, BR-DATA-06) — it is the only
 * party that knows what was switched off a second ago, and the only one whose
 * answer cannot go stale between render and submit.
 *
 * Conventions deliberately shared with `<Typeahead>` so the two feel like one
 * product: the panel is `role="listbox"` under a `role="combobox"` input,
 * arrows move, Enter takes, Escape closes without changing the value, and
 * every edge is a logical property (`start-0`/`end-0`, `text-start`) so it
 * flips with an RTL document rather than being mirrored by hand.
 */

export interface CurrencyFieldProps {
  /** The code the record holds. Uppercased by the caller's `onChange`. */
  value: string;
  onChange: (code: string) => void;
  /** The tenant's offerable codes — `useCurrencyPickerCodes()`. */
  options: readonly string[];
  /** Names the control for a screen reader, and for every test that finds it. */
  label: string;
  id?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

export function CurrencyField({
  value,
  onChange,
  options,
  label,
  id,
  disabled,
  className,
}: CurrencyFieldProps) {
  const reactId = React.useId();
  const listId = `${id ?? reactId}-list`;
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);

  /**
   * Suggestions narrow as the user types, but an EMPTY result closes the panel
   * rather than drawing a "nothing matched" row: unlike a record picker, typing
   * something the list does not hold is a legal answer here, so telling the
   * user it matched nothing would be scolding them for a valid entry.
   */
  const matches = React.useMemo(() => {
    const typed = value.trim().toUpperCase();
    if (typed === '') return [...options];
    return options.filter((code) => code.startsWith(typed) && code !== typed);
  }, [options, value]);

  React.useEffect(() => {
    // A new result set invalidates the old cursor.
    setActive(0);
  }, [matches.length]);

  const showPanel = open && matches.length > 0;

  function commit(code: string) {
    onChange(code);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (matches.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + matches.length) % matches.length);
      return;
    }
    if (event.key === 'Enter') {
      // Only swallow Enter when a suggestion is genuinely highlighted —
      // otherwise the form's own Enter stops working inside the field.
      if (!showPanel) return;
      const code = matches[active];
      if (code === undefined) return;
      event.preventDefault();
      commit(code);
      return;
    }
    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      // Escape closes the suggestions and KEEPS what was typed — the value is
      // the text, so abandoning it would discard the user's own answer.
      setOpen(false);
      return;
    }
    if (event.key === 'Tab') setOpen(false);
  }

  return (
    <div
      className={cn('relative', className)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <input
        id={id}
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showPanel ? `${listId}-${active}` : undefined}
        autoComplete="off"
        // GEN-107's code format. `maxLength` rather than a validator because
        // the screens own their own error sentences.
        maxLength={3}
        disabled={disabled}
        value={value}
        onChange={(event) => {
          setOpen(true);
          onChange(event.target.value.toUpperCase());
        }}
        onFocus={() => setOpen(true)}
        onMouseDown={() => setOpen(true)}
        onKeyDown={onKeyDown}
        data-slot="currency-field"
        className={cn(
          'flex h-12 w-full min-w-0 rounded-[11px] border border-input bg-transparent px-3 py-1 text-base text-start transition-colors outline-none',
          'placeholder:text-muted-foreground',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
          'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        )}
      />

      {showPanel ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute start-0 end-0 z-50 mt-1 max-h-[280px] overflow-y-auto rounded-[11px] border border-input bg-popover py-1 text-sm shadow-lg"
        >
          {matches.map((code, index) => (
            <li
              key={code}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={code === value}
              // The row takes the click, never the focus.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => commit(code)}
              onMouseEnter={() => setActive(index)}
              className={cn(
                'cursor-pointer px-3 py-2 text-start',
                index === active ? 'bg-accent text-accent-foreground' : undefined,
              )}
            >
              {code}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
